import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo, breadcrumbLd } from "../components/Seo";
import { fetchBlogPosts } from "../lib/api";
import { ArrowRight, Calendar } from "@phosphor-icons/react";

// Public Flycher blog index. Pulls from /api/blog (merged local + rankai
// feed under the hood — the /blog page just consumes the shared shape).
// Server-side pagination is 1-based; we load 30 rows per page which is
// plenty for a scannable list and still tiny over the wire.

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [state, setState] = useState({ loading: true, posts: [], page: 1, totalPages: 1 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBlogPosts(state.page, 30);
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          posts: data.posts || [],
          totalPages: data.totalPages || 1,
        }));
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false, posts: [] }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.page]);

  const jsonLd = breadcrumbLd([
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
  ]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="blog-page">
      <Seo
        title="Travel Blog for Indian Travelers — AI Itineraries, Visa Rules, Budgets"
        description="Honest travel guides for Indians — AI itinerary walkthroughs, INR budget breakdowns, visa rules, forex tips and destination deep-dives. Updated twice a day by the Flycher team."
        path="/blog"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 md:px-8 py-10 md:py-16">
        {/* Header */}
        <header className="mb-10 md:mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
            Flycher Blog
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-slate-900 leading-[1.05] mb-4">
            Real travel guides. <span className="text-orange-600">Written for India.</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl">
            No influencer fluff, no USD prices. Honest itineraries, budgets in INR, visa truth, and destination deep-dives — updated twice a day.
          </p>
        </header>

        {/* Post list */}
        {state.loading ? (
          <div className="grid gap-6 md:gap-8">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white rounded-2xl border border-slate-200 h-40" />
            ))}
          </div>
        ) : state.posts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
            No posts yet — check back soon. In the meantime,{" "}
            <Link to="/itinerary" className="text-orange-600 underline">
              try our free AI itinerary planner
            </Link>
            .
          </div>
        ) : (
          <ul className="grid gap-6 md:gap-8" data-testid="blog-post-list">
            {state.posts.map((p, idx) => (
              <li key={p.slug || idx}>
                <Link
                  to={`/blog/${p.slug}`}
                  className="group block bg-white rounded-2xl border border-slate-200 hover:border-orange-400 hover:shadow-lg transition-all overflow-hidden"
                  data-testid={`blog-post-card-${p.slug}`}
                >
                  <div className="md:flex">
                    {p.featured_image_url && (
                      <div className="md:w-56 aspect-[16/9] md:aspect-[4/3] overflow-hidden bg-slate-100 shrink-0">
                        <img
                          src={p.featured_image_url}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="flex-1 p-5 md:p-6">
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                        {p.published_at && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={12} weight="bold" />
                            {fmtDate(p.published_at)}
                          </span>
                        )}
                        {p.author_name && <span>· {p.author_name}</span>}
                      </div>
                      <h2 className="text-xl md:text-2xl font-semibold text-slate-900 group-hover:text-orange-600 transition-colors leading-snug mb-2">
                        {p.title}
                      </h2>
                      {p.excerpt && (
                        <p className="text-sm md:text-base text-slate-600 leading-relaxed line-clamp-3">
                          {p.excerpt}
                        </p>
                      )}
                      <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600">
                        Read full guide
                        <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {state.totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-3 text-sm">
            <button
              type="button"
              disabled={state.page <= 1}
              onClick={() => setState((s) => ({ ...s, page: Math.max(1, s.page - 1), loading: true }))}
              className="px-4 py-2 border border-slate-300 rounded-full disabled:opacity-40 hover:bg-white transition"
              data-testid="blog-pagination-prev"
            >
              ← Newer
            </button>
            <span className="text-slate-500 tabular-nums">
              Page {state.page} of {state.totalPages}
            </span>
            <button
              type="button"
              disabled={state.page >= state.totalPages}
              onClick={() => setState((s) => ({ ...s, page: Math.min(s.totalPages, s.page + 1), loading: true }))}
              className="px-4 py-2 border border-slate-300 rounded-full disabled:opacity-40 hover:bg-white transition"
              data-testid="blog-pagination-next"
            >
              Older →
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
